import axios from 'axios';
import { NotificationManager } from 'react-notifications';
import Cookies from 'js-cookie';
import { getRecaptchaToken } from '../helpers_components/recaptchaService';
import { handleSessionExpired, isSessionInvalidResponse } from '../../services/sessionExpiration';

// Endpoints públicos protegidos con reCAPTCHA v3 en el backend (core). Cada uno usa su
// propia acción, validada server-side por el middleware `recaptcha:<accion>`. El orden
// importa: `documents/find` se evalúa antes que `find`, y `find` excluye `find/number`
// y `find/matter` (que NO están protegidos) con lookahead negativo.
const RECAPTCHA_PROTECTED_ENDPOINTS = [
  { pattern: /\/courts_matters\/courts_parties\//, action: 'consult_courts_by_matter' },
  { pattern: /\/electronic_expedients\/by_date\//, action: 'consult_by_date' },
  { pattern: /\/electronic_expedients\/by_names\//, action: 'consult_by_names' },
  { pattern: /\/electronic_expedients\/documents\/find\//, action: 'consult_documents' },
  { pattern: /\/electronic_expedients\/find\/(?!number\/)(?!matter\/)/, action: 'consult_find' },
  { pattern: /\/courts\/get_list\//, action: 'consult_courts_list' },
];

export class NewClassRequest {
  constructor(url, method, headers, bodyRequest) {
    this.url = url;
    this.bodyRequest = bodyRequest;
    this.method = method;
    this.headers = headers;
    this.queryRetryCounter = 0;
    // No reintentar ante fallos de autenticación: una sesión invalidada debe
    // redirigir a login de inmediato, sin acumular peticiones ni errores.
    this.queryRetryLimit = 0;
  }

  async executeRequest(_show = true, token = null) {
    let responseError = {
      code: 800,
      message:
        'Hubo un error al realizar la accion, intentelo mas tarde o contacte al administrador.',
    };

    const usingPublicToken = !(
      Cookies.get('authentication_token_02') && !this.usePublicToken(window.location.pathname)
    );

    let authenticationToken = usingPublicToken
      ? btoa(process.env.REACT_APP_TOKEN_WITHOUT_AUTHENTICATION)
      : String(Cookies.get('authentication_token_02'));

    if (token) {
      authenticationToken = token;
    }

    const requestHeaders = {
      Authorization: authenticationToken,
    };

    // Consultas públicas a endpoints protegidos: adjuntamos un token de reCAPTCHA v3
    // FRESCO por request, con la acción propia del endpoint (el backend valida que
    // coincida). Solo se pide para endpoints protegidos, para no gastar ejecuciones
    // de reCAPTCHA en llamadas que el backend no valida.
    if (usingPublicToken) {
      const protectedEndpoint = RECAPTCHA_PROTECTED_ENDPOINTS.find((endpoint) =>
        endpoint.pattern.test(this.url)
      );
      if (protectedEndpoint) {
        const recaptchaToken = await getRecaptchaToken(protectedEndpoint.action);
        if (recaptchaToken) {
          requestHeaders['X-Recaptcha-Token'] = recaptchaToken;
        }
      }
    }

    try {
      const response = await axios({
        method: this.method,
        url: this.url,
        data: this.bodyRequest,
        headers: requestHeaders,
      });
      const { clean } =
        response && response.data && response.data.data ? response.data.data : { clean: false };
      if (clean) {
        // Sesión invalidada (p. ej. login en otro navegador): cierre controlado
        // y redirección a login. Sin toasts técnicos ni reintentos.
        handleSessionExpired();
        return {
          code: 403,
          response: null,
          completeResponse: null,
        };
      }

      if (response.status === 200) {
        return {
          code: response.data.code,
          response: response.data.data,
          completeResponse: response,
        };
      }
    } catch (error) {
      // Los fallos de autenticación (CheckJWT) llegan con status HTTP real
      // (401/403); axios los rechaza y caen aquí. Detectamos la sesión inválida
      // por status o por el flag `clean` del cuerpo y cerramos de forma
      // controlada, sin mostrar errores técnicos ni reintentar.
      const status = error.response ? error.response.status : null;
      const errorData =
        error.response && error.response.data && error.response.data.data
          ? error.response.data.data
          : { clean: false };

      if (isSessionInvalidResponse(status, errorData)) {
        handleSessionExpired();
        return {
          code: status || 403,
          response: null,
          completeResponse: null,
        };
      }

      // Rate-limit por IP (throttle de Laravel): cuerpo { message: "Too Many Attempts." }.
      if (status === 429) {
        const message =
          'Has realizado demasiadas consultas en poco tiempo. Espera un momento e inténtalo de nuevo.';
        NotificationManager.error(message);
        return {
          code: 429,
          response: { message },
          completeResponse: null,
          alreadyNotified: true,
        };
      }

      // Errores controlados del backend (reCAPTCHA: 403 inválido/sin token, 503 Google no disponible).
      // Mostramos el mensaje del servidor y marcamos alreadyNotified para no duplicar avisos.
      if ((status === 403 || status === 503) && errorData && errorData.error) {
        NotificationManager.error(errorData.error);
        return {
          code: status,
          response: { message: errorData.error, error: true },
          completeResponse: null,
          alreadyNotified: true,
        };
      }

      responseError = {
        code: 500,
        message: 'El servidor no responde',
      };

      NotificationManager.error(responseError.message, 'Error ' + responseError.code + ':');
    }

    return {
      code: responseError.code,
      response: { message: responseError.message },
      completeResponse: null,
    };
  }

  usePublicToken(pathname) {
    const publicPaths = ['/boletin_judicial/consultar', '/expedientes/insertar'];
    return publicPaths.includes(pathname);
  }
}
